import { useEffect } from "react";

const STYLE_ELEMENT_ID = "certificate-fonts";

// Self-hosted in public/fonts (see docs/fonts.md). Raleway is already loaded
// app-wide in public/index.html.
const CERTIFICATE_FONT_FACES = [
  { family: "Playfair Display", file: "playfair-display-v40-latin.woff2", weight: "400 700" },
  { family: "Lora", file: "lora-v37-latin.woff2", weight: "400 700" },
  { family: "Montserrat", file: "montserrat-v31-latin.woff2", weight: "400 700" },
  { family: "Great Vibes", file: "great-vibes-v21-latin.woff2", weight: "400" },
  { family: "Nunito", file: "nunito-v32-latin.woff2", weight: "400 700" },
  { family: "UnifrakturMaguntia", file: "unifrakturmaguntia-v22-latin.woff2", weight: "400" },
  { family: "Oswald", file: "oswald-v57-latin.woff2", weight: "400 700" },
];

/** Raleway's latin file, declared in index.html; listed here for exported files. */
const RALEWAY_FACE = { family: "Raleway", file: "raleway-v37-latin.woff2", weight: "400 700" };

type CertificateFontFace = (typeof CERTIFICATE_FONT_FACES)[number];

/** Where a font file is served from. */
export function certificateFontUrl(file: string): string {
  return `${process.env.PUBLIC_URL ?? ""}/fonts/${file}`;
}

function fontFaceCss(faces: CertificateFontFace[], src: (file: string) => string): string {
  return faces
    .map(
      ({ family, file, weight }) => `@font-face {
  font-family: "${family}";
  font-style: normal;
  font-weight: ${weight};
  font-display: swap;
  src: url("${src(file)}") format("woff2");
}`
    )
    .join("\n");
}

/**
 * Every font a certificate can use, Raleway included, as @font-face CSS with
 * each file's address given by `src`, e.g. inlined as a data URL.
 */
export function allCertificateFontsCss(src: (file: string) => string): string {
  return fontFaceCss([...CERTIFICATE_FONT_FACES, RALEWAY_FACE], src);
}

/** Every file allCertificateFontsCss refers to. */
export const ALL_CERTIFICATE_FONT_FILES = [...CERTIFICATE_FONT_FACES, RALEWAY_FACE].map(
  (face) => face.file
);

/**
 * Declares the certificate fonts on the page, once. The browser downloads a
 * font file the first time a certificate uses it.
 */
export function installCertificateFonts(): void {
  if (document.getElementById(STYLE_ELEMENT_ID)) return;

  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = fontFaceCss(CERTIFICATE_FONT_FACES, certificateFontUrl);
  document.head.appendChild(style);
}

/**
 * Makes the certificate fonts available. Called by Certificates pages only, so
 * the rest of the dashboard never declares them.
 */
export function useCertificateFonts(): void {
  useEffect(installCertificateFonts, []);
}
